// src/components/AvatarPicker/AvatarPickerModal.jsx
import { useState, useEffect } from "react";
import "../styles/Avatarpicker.css";
import { createPortal } from "react-dom";

const FACES = [
  { id: "neutral", label: "NEUTRAL" },
  { id: "serious", label: "SERIOUS" },
  { id: "fierce",  label: "FIERCE"  },
  { id: "cool",    label: "COOL"    },
];

const VISORS = [
  { id: "none",  label: "NONE",  color: null },
  { id: "clear", label: "CLEAR", color: "rgba(200,235,255,0.28)" },
  { id: "smoke", label: "SMOKE", color: "rgba(20,20,40,0.72)" },
  { id: "gold",  label: "GOLD",  color: "rgba(200,155,20,0.58)" },
  { id: "red",   label: "RED",   color: "rgba(210,50,10,0.52)" },
  { id: "blue",  label: "BLUE",  color: "rgba(0,150,255,0.52)" },
];

export const SUIT_COLORS = [
  { id: "carbon", label: "CARBON", base: "#1c1c1c", accent: "#FF3800" },
  { id: "white",  label: "WHITE",  base: "#D8D6D0", accent: "#FF3800" },
  { id: "red",    label: "RED",    base: "#BB1A00", accent: "#ffffff" },
  { id: "blue",   label: "BLUE",   base: "#002E7A", accent: "#00D4FF" },
  { id: "green",  label: "GREEN",  base: "#083818", accent: "#a3e635" },
  { id: "gold",   label: "GOLD",   base: "#7A5800", accent: "#FFD700" },
];

const SKIN_TONES = [
  { id: "light",  label: "LIGHT",  color: "#F5CBA7" },
  { id: "medium", label: "MED",    color: "#C68642" },
  { id: "tan",    label: "TAN",    color: "#A0522D" },
  { id: "dark",   label: "DARK",   color: "#5C3317" },
];

export function DriverAvatarSVG({ face, visor, suit, skin, number, size = 120 }) {
  const suitDef  = SUIT_COLORS.find(s => s.id === suit)  || SUIT_COLORS[0];
  const visorDef = VISORS.find(v => v.id === visor)       || VISORS[0];
  const skinDef  = SKIN_TONES.find(s => s.id === skin)    || SKIN_TONES[1];
  const sc       = skinDef.color;
  const clipId   = `pc-${size}-${suit}`;

  const brows = {
    neutral: "M30 38 Q36 36 42 38 M46 38 Q52 36 58 38",
    serious: "M29 36 Q36 38 42 37 M46 37 Q52 38 59 36",
    fierce:  "M29 38 Q36 34 42 37 M46 37 Q52 34 59 38",
    cool:    "M30 38 Q36 36 42 38 M46 38 Q52 36 58 38",
  };
  const mouths = {
    neutral: "M36 54 Q44 58 52 54",
    serious: "M36 54 Q44 54 52 54",
    fierce:  "M35 56 Q44 52 53 56",
    cool:    "M37 55 Q44 59 51 55",
  };
  const browColor = sc === "#F5CBA7" ? "#7a5014" : "#2a1506";
  const lipColor  = sc === "#F5CBA7" ? "#b06040" : "#6a2e18";

  return (
    <svg viewBox="0 0 88 120" width={size} height={size} xmlns="http://www.w3.org/2000/svg" style={{display:"block"}}>
      {/* Shadow */}
      <ellipse cx="44" cy="118" rx="20" ry="2.5" fill="rgba(0,0,0,0.35)"/>
      {/* Body torso */}
      <path d="M20 78 C18 76 16 72 16 68 L16 112 Q16 117 22 117 L66 117 Q72 117 72 112 L72 68 C72 72 70 76 68 78 Z" fill={suitDef.base}/>
      {/* Center stripe */}
      <defs><clipPath id={clipId}><path d="M20 78 C18 76 16 72 16 68 L16 112 Q16 117 22 117 L66 117 Q72 117 72 112 L72 68 C72 72 70 76 68 78 Z"/></clipPath></defs>
      <rect x="41" y="68" width="6" height="50" fill={suitDef.accent} opacity="0.22" clipPath={`url(#${clipId})`}/>
      {/* Collar */}
      <path d="M36 76 L44 81 L52 76 L44 71 Z" fill={suitDef.accent} opacity="0.5"/>
      {/* Left arm */}
      <path d="M20 78 L7 89 Q3 93 5 99 L11 113 Q13 117 18 115 L22 100 Z" fill={suitDef.base}/>
      <ellipse cx="11" cy="114" rx="6" ry="4.5" fill={suitDef.accent} opacity="0.9"/>
      {/* Right arm */}
      <path d="M68 78 L81 89 Q85 93 83 99 L77 113 Q75 117 70 115 L66 100 Z" fill={suitDef.base}/>
      <ellipse cx="77" cy="114" rx="6" ry="4.5" fill={suitDef.accent} opacity="0.9"/>
      {/* Racing number on chest */}
      {number && (
        <text x="44" y="103" textAnchor="middle" fontFamily="'Courier New',monospace"
          fontWeight="900" fontSize="14" fill={suitDef.accent} opacity="0.9" stroke={suitDef.base} strokeWidth="0.5">
          {String(number).slice(0,2)}
        </text>
      )}
      {/* Neck */}
      <rect x="39" y="66" width="10" height="14" rx="3.5" fill={sc}/>
      {/* Helmet shell */}
      <ellipse cx="44" cy="44" rx="22" ry="24" fill={suitDef.base}/>
      <defs><clipPath id={`hc-${clipId}`}><ellipse cx="44" cy="44" rx="22" ry="24"/></clipPath></defs>
      <rect x="41" y="18" width="6" height="54" fill={suitDef.accent} opacity="0.25" clipPath={`url(#hc-${clipId})`}/>
      <ellipse cx="44" cy="44" rx="22" ry="24" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="0.75"/>
      {/* Chin guard */}
      <path d="M26 52 Q24 60 28 66 L36 70 L52 70 L60 66 Q64 60 62 52" fill={suitDef.base} stroke="rgba(255,255,255,0.07)" strokeWidth="0.5"/>
      {/* Face skin area */}
      <path d="M30 38 Q32 31 44 30 Q56 31 58 38 L58 58 Q56 64 44 65 Q32 64 30 58 Z" fill={sc}/>
      {/* Eyes */}
      {face === "serious" ? (
        <><rect x="33" y="41" width="8" height="6" rx="1" fill="#1a1a2e"/><rect x="47" y="41" width="8" height="6" rx="1" fill="#1a1a2e"/></>
      ) : face === "fierce" ? (
        <><path d="M33 46 L41 40 L41 46Z" fill="#1a1a2e"/><path d="M47 46 L55 40 L55 46Z" fill="#1a1a2e"/></>
      ) : face === "cool" ? (
        <><rect x="32" y="42" width="10" height="5" rx="2.5" fill="#1a1a2e"/><rect x="46" y="42" width="10" height="5" rx="2.5" fill="#1a1a2e"/></>
      ) : (
        <><ellipse cx="37" cy="44" rx="4" ry="3.5" fill="#1a1a2e"/><ellipse cx="51" cy="44" rx="4" ry="3.5" fill="#1a1a2e"/></>
      )}
      {/* Eye whites */}
      <ellipse cx="35" cy="43" rx="1.5" ry="1.5" fill="rgba(255,255,255,0.6)"/>
      <ellipse cx="49" cy="43" rx="1.5" ry="1.5" fill="rgba(255,255,255,0.6)"/>
      {/* Brows */}
      <path d={brows[face] || brows.neutral} fill="none" stroke={browColor} strokeWidth="1.5" strokeLinecap="round"/>
      {/* Mouth */}
      <path d={mouths[face] || mouths.neutral} fill="none" stroke={lipColor} strokeWidth="1.2" strokeLinecap="round"/>
      {/* Visor overlay */}
      {visorDef.color && (
        <>
          <path d="M30 38 Q32 31 44 30 Q56 31 58 38 L58 58 Q56 64 44 65 Q32 64 30 58 Z" fill={visorDef.color}/>
          <path d="M32 35 Q38 32 50 33" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeLinecap="round"/>
        </>
      )}
      {/* Helmet side lines */}
      <path d="M28 44 Q26 52 28 58" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.8"/>
      <path d="M60 44 Q62 52 60 58" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.8"/>
    </svg>
  );
}

export function MiniHelmet({ avatar }) {
  return (
    <DriverAvatarSVG
      face={avatar?.face   || "neutral"}
      visor={avatar?.visor || "none"}
      suit={avatar?.suit   || "carbon"}
      skin={avatar?.skin   || "medium"}
      number={avatar?.number || ""}
      size={64}
    />
  );
}

export default function AvatarPickerModal({ value = {}, onConfirm, onClose }) {
  const [face,   setFace]   = useState(value.face   || "neutral");
  const [visor,  setVisor]  = useState(value.visor  || "none");
  const [suit,   setSuit]   = useState(value.suit   || "carbon");
  const [skin,   setSkin]   = useState(value.skin   || "medium");
  const [number, setNumber] = useState(value.number || "");

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", handler); document.body.style.overflow = ""; };
  }, [onClose]);

  const handleConfirm = () => onConfirm({ face, visor, suit, skin, number });
  const suitDef = SUIT_COLORS.find(s => s.id === suit) || SUIT_COLORS[0];
  const faceDef = FACES.find(f => f.id === face) || FACES[0];

  return createPortal(
    <div className="vk-modal-backdrop" onClick={onClose}>
      <div className="vk-modal-panel" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Customize Avatar">
        <div className="vk-modal-header">
          <div className="vk-modal-header-left">
            <span className="vk-modal-logo">VK</span>
            <div>
              <div className="vk-modal-eyebrow">// AVATAR CUSTOMIZER</div>
              <div className="vk-modal-title">DRIVER PROFILE</div>
            </div>
          </div>
          <button className="vk-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="vk-modal-body">
          <div className="vk-modal-preview-col">
            <div className="vk-modal-preview-box">
              <div className="vk-modal-preview-scan"/>
              <div className="vk-modal-preview-helmet">
                <DriverAvatarSVG face={face} visor={visor} suit={suit} skin={skin} number={number} size={130}/>
              </div>
              <div className="vk-modal-preview-meta">
                <span className="vk-modal-preview-shell">{suitDef.label}</span>
                <span className="vk-modal-preview-sep">·</span>
                <span className="vk-modal-preview-style">{faceDef.label}</span>
              </div>
              <div className="vk-modal-preview-tag">// LIVE PREVIEW</div>
            </div>
          </div>
          <div className="vk-modal-controls-col">
            {/* FACE STYLE */}
            <div className="vk-modal-section">
              <div className="vk-modal-section-label">FACE STYLE</div>
              <div className="vk-modal-helmet-grid">
                {FACES.map(f => (
                  <button key={f.id} className={`vk-modal-helmet-btn${face === f.id ? " active" : ""}`} onClick={() => setFace(f.id)}>
                    <div className="vk-modal-helmet-btn-preview">
                      <DriverAvatarSVG face={f.id} visor="none" suit={suit} skin={skin} number="" size={46}/>
                    </div>
                    <span className="vk-modal-helmet-btn-label">{f.label}</span>
                  </button>
                ))}
              </div>
            </div>
            {/* SKIN TONE */}
            <div className="vk-modal-section">
              <div className="vk-modal-section-label">SKIN TONE</div>
              <div className="vk-modal-shell-grid">
                {SKIN_TONES.map(s => (
                  <button key={s.id} className={`vk-modal-shell-btn${skin === s.id ? " active" : ""}`}
                    style={{"--shell-stripe": s.color}} onClick={() => setSkin(s.id)}>
                    <span className="vk-modal-shell-swatch" style={{background: s.color, borderColor: skin === s.id ? s.color : "rgba(255,255,255,0.1)"}}/>
                    <span className="vk-modal-shell-name">{s.label}</span>
                  </button>
                ))}
              </div>
            </div>
            {/* SUIT COLOR */}
            <div className="vk-modal-section">
              <div className="vk-modal-section-label">SUIT COLOR</div>
              <div className="vk-modal-shell-grid">
                {SUIT_COLORS.map(s => (
                  <button key={s.id} className={`vk-modal-shell-btn${suit === s.id ? " active" : ""}`}
                    style={{"--shell-base": s.base, "--shell-stripe": s.accent}} onClick={() => setSuit(s.id)}>
                    <span className="vk-modal-shell-swatch" style={{background: s.base, borderColor: suit === s.id ? s.accent : "rgba(255,255,255,0.1)"}}/>
                    <span className="vk-modal-shell-name">{s.label}</span>
                  </button>
                ))}
              </div>
            </div>
            {/* VISOR */}
            <div className="vk-modal-section">
              <div className="vk-modal-section-label">VISOR TINT</div>
              <div className="vk-modal-visor-grid">
                {VISORS.map(v => (
                  <button key={v.id} className={`vk-modal-visor-btn${visor === v.id ? " active" : ""}`} onClick={() => setVisor(v.id)}>
                    <span className="vk-modal-visor-swatch" style={{background: v.color ? (v.id==="clear" ? "rgba(200,235,255,0.5)" : v.color) : "rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.15)"}}/>
                    <span className="vk-modal-visor-name">{v.label}</span>
                  </button>
                ))}
              </div>
            </div>
            {/* NUMBER */}
            <div className="vk-modal-section">
              <div className="vk-modal-section-label">RACING NUMBER <span className="vk-modal-optional">(OPTIONAL)</span></div>
              <div className="vk-modal-number-row">
                {["","1","3","7","23","69","99"].map(n => (
                  <button key={n} className={`vk-modal-num-btn${number===n?" active":""}`} onClick={() => setNumber(n)}>
                    {n===""?"—":n}
                  </button>
                ))}
                <input className="vk-modal-num-input" placeholder="##" maxLength={2}
                  value={number} onChange={e => setNumber(e.target.value.replace(/\D/g,""))}/>
              </div>
            </div>
          </div>
        </div>
        <div className="vk-modal-footer">
          <button className="vk-modal-cancel" onClick={onClose}>CANCEL</button>
          <button className="vk-modal-confirm" onClick={handleConfirm}>CONFIRM AVATAR ▶</button>
        </div>
      </div>
    </div>,
    document.body
  );
}